import { useState, useEffect, useRef } from 'react';
import { usersAPI } from '../lib/api';
import { Input } from './ui/input';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Search, X, Loader2 } from 'lucide-react';

/**
 * MemberPicker — search-as-you-type alumni member selector.
 *
 * Used for assigning a member as event host. Will be reused anywhere we need
 * to attach a structured user reference to content (event host, group admin,
 * mentor assignment, etc.).
 *
 * Props:
 *   value: user object or null — the currently selected member
 *   onChange: (user|null) => void — fires when the selection changes
 *   placeholder: string
 */
export default function MemberPicker({ value, onChange, placeholder = 'Search alumni by name...' }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  // Close the dropdown when clicking outside
  useEffect(() => {
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Debounced search — 250ms after the user stops typing
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await usersAPI.listUsers({ search: query.trim(), limit: 8 });
        setResults(Array.isArray(res.data) ? res.data : []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query]);

  const handleSelect = (user) => {
    onChange(user);
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  const handleClear = () => {
    onChange(null);
    setQuery('');
    setResults([]);
  };

  // If a member is already selected, render a chip with their name + avatar
  if (value) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
        <Avatar className="w-7 h-7">
          <AvatarImage src={value.profile_photo} />
          <AvatarFallback className="bg-mint-fresh text-dark-slate text-xs">{value.name?.charAt(0)}</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-dark-slate truncate">{value.name}</p>
          {(value.title || value.institution) && (
            <p className="text-[11px] text-slate-gray truncate">
              {[value.title, value.institution].filter(Boolean).join(' · ')}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={handleClear}
          className="text-slate-gray hover:text-red-500 transition-colors"
          aria-label="Remove selected member"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-gray" />
        <Input
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className="pl-9 rounded-xl"
        />
        {loading && (
          <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-gray animate-spin" />
        )}
      </div>

      {open && (query.trim() || results.length > 0) && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-64 overflow-y-auto">
          {results.length === 0 && !loading && (
            <p className="px-3 py-3 text-sm text-slate-gray">No alumni found.</p>
          )}
          {results.map(u => (
            <button
              key={u.user_id}
              type="button"
              onClick={() => handleSelect(u)}
              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-50 transition-colors text-left"
            >
              <Avatar className="w-8 h-8 flex-shrink-0">
                <AvatarImage src={u.profile_photo} />
                <AvatarFallback className="bg-mint-fresh text-dark-slate text-xs">{u.name?.charAt(0)}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-dark-slate truncate">{u.name}</p>
                {(u.title || u.institution) && (
                  <p className="text-[11px] text-slate-gray truncate">
                    {[u.title, u.institution].filter(Boolean).join(' · ')}
                  </p>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
